import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Paper,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
  useTheme as useMuiTheme,
  SvgIcon
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { 
  Email as EmailIcon, 
  Phone as PhoneIcon, 
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  Telegram as TelegramIcon,
  CloudDownload as CloudDownloadIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { SiSkype, SiDiscord, SiSlack } from 'react-icons/si';

// Custom Discord Icon
const DiscordIcon = (props) => (
  <SvgIcon 
    {...props} 
    sx={{ 
      fontSize: 'inherit',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& > svg': {
        width: '100%',
        height: '100%',
      },
      ...props.sx
    }}
    viewBox="0 0 24 24"
  >
    <SiDiscord />
  </SvgIcon>
);

// Custom Skype Icon
const SkypeIcon = (props) => (
  <SvgIcon 
    {...props}
    sx={{ 
      fontSize: 'inherit',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& > svg': {
        width: '100%',
        height: '100%',
      },
      ...props.sx
    }}
    viewBox="0 0 24 24"
  >
    <SiSkype />
  </SvgIcon>
);

// Custom Slack Icon
const SlackIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </SvgIcon>
);

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '2px',
  },
}));

const ContactTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    '& fieldset': {
      borderColor: theme.palette.mode === 'light' 
        ? 'rgba(0, 0, 0, 0.1)'
        : 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const ContactButton = styled(Button)(({ theme }) => ({
  padding: '12px 30px',
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: '30px',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  transition: 'all 0.3s ease',
  boxShadow: '0 10px 20px rgba(106, 73, 219, 0.3)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 25px rgba(106, 73, 219, 0.4)',
  },
}));

const DownloadCVButton = styled(Button)(({ theme }) => ({
  borderRadius: '30px',
  padding: '12px 25px',
  backgroundColor: 'transparent',
  color: theme.palette.text.primary,
  border: `2px solid ${theme.palette.primary.main}`,
  transition: 'all 0.3s ease',
  fontWeight: 500,
  marginBottom: theme.spacing(3),
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(106, 73, 219, 0.3)',
  },
}));

const ContactInfoItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.mode === 'light' 
    ? 'rgba(0, 0, 0, 0.05)' 
    : 'rgba(255, 255, 255, 0.05)',
  margin: theme.spacing(0.5),
  padding: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 20px rgba(106, 73, 219, 0.3)',
  },
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0.3),
    padding: theme.spacing(1.2),
  },
}));

const ContactFormContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  backgroundColor: theme.palette.background.card,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const formRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simulate form submission
    setTimeout(() => {
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        message: ''
      });
    }, 1000);
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(false);
  };

  const socialLinks = [
    { 
      platform: 'Instagram', 
      icon: <InstagramIcon />, 
      url: 'https://www.instagram.com/mahesh_messi108' 
    },
    { 
      platform: 'LinkedIn', 
      icon: <LinkedInIcon />, 
      url: 'https://www.linkedin.com/in/siva-mahesh' 
    },
    { 
      platform: 'GitHub', 
      icon: <GitHubIcon />, 
      url: 'https://github.com/Mahesh1772' 
    },
    { 
      platform: 'Skype', 
      icon: <SiSkype style={{ fontSize: '1.5rem' }} />, 
      url: 'skype:live:ironmanmark3armour' 
    },
    { 
      platform: 'Slack', 
      icon: <SiSlack style={{ fontSize: '1.5rem' }} />, 
      url: 'https://electricfriendsas.slack.com/team/U06AYNMAE70' 
    }
  ];

  return (
    <Box 
      id="contact" 
      sx={{ 
        py: { xs: 6, md: 12 }, 
        backgroundColor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <SectionTitle
              variant="h2"
              component="h2"
              sx={{ 
                fontWeight: 700,
                color: 'primary.main',
                fontSize: { xs: '2rem', md: '3rem' }
              }}
            >
              Contact Me
            </SectionTitle>
          </motion.div>
        </Box>

        <Grid container spacing={{ xs: 4, md: 6 }}>
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    mb: 4, 
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '1.5rem', md: '2rem' }
                  }}
                >
                  Get In Touch
                </Typography>

                <ContactInfoItem>
                  <EmailIcon 
                    sx={{ 
                      mr: 2, 
                      color: 'primary.main', 
                      fontSize: 28 
                    }} 
                  />
                  <Typography variant="body1">
                    sivagnanam.mahehswaran@u.nus.edu
                  </Typography>
                </ContactInfoItem>

                <ContactInfoItem>
                  <Box 
                    sx={{ 
                      mr: 2, 
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '28px'
                    }}
                  >
                    <SiDiscord style={{ fontSize: '1.5rem' }} />
                  </Box>
                  <Typography variant="body1">
                    mahesh3808
                  </Typography>
                </ContactInfoItem>

                <Box sx={{ mt: 4, mb: 5 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 2,
                      fontWeight: 600,
                      color: 'text.primary'
                    }}
                  >
                    Follow Me
                  </Typography>
                  <Box>
                    {socialLinks.map((link, index) => (
                      <SocialIconButton
                        key={index}
                        component="a"
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={link.platform}
                      >
                        {link.icon}
                      </SocialIconButton>
                    ))}
                  </Box>
                </Box>

                <DownloadCVButton
                  component="a"
                  href={process.env.PUBLIC_URL + "/Images/Mahesh_Resume.pdf"}
                  download
                  startIcon={<CloudDownloadIcon />}
                  sx={{
                    px: { xs: 3, md: 4 },
                    py: { xs: 1, md: 1.5 },
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                >
                  Download CV
                </DownloadCVButton>
              </Box>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <ContactFormContainer elevation={3}>
                <form ref={formRef} onSubmit={handleSubmit}>
                  <ContactTextField
                    fullWidth
                    label="Your Name"
                    name="name"
                    variant="outlined"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ color: 'primary.main' }}>
                          <Typography variant="body2" sx={{ color: 'primary.main' }}>|</Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <ContactTextField
                    fullWidth
                    label="Your Email"
                    name="email"
                    variant="outlined"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ color: 'primary.main' }}>
                          <Typography variant="body2" sx={{ color: 'primary.main' }}>|</Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <ContactTextField
                    fullWidth
                    label="Your Message"
                    name="message"
                    variant="outlined"
                    multiline
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ color: 'primary.main', alignSelf: 'flex-start', mt: 1.5 }}>
                          <Typography variant="body2" sx={{ color: 'primary.main' }}>|</Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box sx={{ textAlign: 'right', mt: 2 }}>
                    <ContactButton
                      type="submit"
                      variant="contained"
                      endIcon={<SendIcon />}
                      sx={{
                        px: { xs: 3, md: 4 },
                        py: { xs: 1, md: 1.5 },
                        fontSize: { xs: '0.875rem', md: '1rem' }
                      }}
                    >
                      Send Message
                    </ContactButton>
                  </Box>
                </form>
              </ContactFormContainer>
            </motion.div>
          </Grid>
        </Grid>

        <Snackbar 
          open={success} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="success" 
            variant="filled"
            sx={{ width: '100%' }}
          >
            Your message was sent successfully!
          </Alert>
        </Snackbar>

        <Snackbar 
          open={error} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="error" 
            variant="filled"
            sx={{ width: '100%' }}
          >
            There was an error sending your message. Please try again.
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Contact; 